import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../../components/Navbar/Navbar";
import Home from "./components/Home";
import Menus from "./components/Menus";
import Footer from "../../components/Footer/Footer";
import CustomerMobileModal from "../../components/CustomerMobileModal/CustomerMobileModal";
import { useLanding } from "./_hooks/useLanding";
import { getStoreInfo } from "../../utils/utils";
import { OrderService } from "../../utils/api.services";
import "./Landing.scss";

const Landing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tableParam = searchParams.get("table") || searchParams.get("tableId");

  const { banners, menuItems, menuCategories } = useLanding({
    callAPI: false,
  });

  const reduxRestaurantInfo = useSelector((state) => state.data?.restaurantInfo);
  const storeInfo = reduxRestaurantInfo || getStoreInfo();
  const visibleSections = storeInfo?.visibleSections || {};
  const isOrderFlow = storeInfo?.workflowPlan === "ORDER FLOW";

  const storeStatus = localStorage.getItem("storeInfo");
  const [activeOrderToken, setActiveOrderToken] = useState(
    () => localStorage.getItem("currentOrderToken") || "Active"
  );
  const [hasActiveOrder, setHasActiveOrder] = useState(
    () => !!localStorage.getItem("currentOrder")
  );

  const [showMobileModal, setShowMobileModal] = useState(() => {
    const saved = localStorage.getItem("customerMobile") || sessionStorage.getItem("customerMobile");
    const savedTimestamp = localStorage.getItem("customerMobileTimestamp") || sessionStorage.getItem("customerMobileTimestamp");
    const isExpired = !savedTimestamp || (Date.now() - Number(savedTimestamp) > 24 * 60 * 60 * 1000);
    if (isExpired && saved) {
      localStorage.removeItem("customerMobile");
      sessionStorage.removeItem("customerMobile");
      localStorage.removeItem("customerMobileTimestamp");
      sessionStorage.removeItem("customerMobileTimestamp");
      localStorage.removeItem("currentOrder");
      localStorage.removeItem("currentOrderToken");
      return storeStatus !== "INACTIVE";
    }
    return !saved && storeStatus !== "INACTIVE";
  });

  const [showBackToTop, setShowBackToTop] = useState(false);

  // Capture table param from QR scan URL and persist
  useEffect(() => {
    if (tableParam) {
      sessionStorage.setItem("scannedTableId", tableParam);
      localStorage.setItem("scannedTableId", tableParam);
    }
  }, [tableParam]);

  useEffect(() => {
    const handleWindowScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleWindowScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleWindowScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const saved = localStorage.getItem("customerMobile") || sessionStorage.getItem("customerMobile");
    const savedTimestamp = localStorage.getItem("customerMobileTimestamp") || sessionStorage.getItem("customerMobileTimestamp");
    const isExpired = !savedTimestamp || (Date.now() - Number(savedTimestamp) > 24 * 60 * 60 * 1000);
    if (isExpired && saved) {
      localStorage.removeItem("customerMobile");
      sessionStorage.removeItem("customerMobile");
      localStorage.removeItem("customerMobileTimestamp");
      sessionStorage.removeItem("customerMobileTimestamp");
      localStorage.removeItem("currentOrder");
      localStorage.removeItem("currentOrderToken");
    }

    const savedMobile = localStorage.getItem("customerMobile") || sessionStorage.getItem("customerMobile");
    if (!savedMobile && storeStatus !== "INACTIVE") {
      setShowMobileModal(true);
    }

    const storeId = storeInfo?._id;
    if (savedMobile && storeId) {
      OrderService.getActiveOrderByMobile(storeId, savedMobile)
        .then((res) => {
          if (res?.data?.activeOrder) {
            setHasActiveOrder(true);
            setActiveOrderToken(res.data.activeOrder.token || "Active");
            localStorage.setItem("currentOrder", res.data.activeOrder._id);
            localStorage.setItem("currentOrderToken", res.data.activeOrder.token || "Active");
          } else {
            const hadStoredOrder = localStorage.getItem("currentOrder");
            setHasActiveOrder(false);
            setActiveOrderToken("");
            localStorage.removeItem("currentOrder");
            localStorage.removeItem("currentOrderToken");
            if (hadStoredOrder) {
              localStorage.removeItem("customerMobile");
              localStorage.removeItem("customerMobileTimestamp");
              sessionStorage.removeItem("customerMobile");
              sessionStorage.removeItem("customerMobileTimestamp");
              if (storeStatus !== "INACTIVE") {
                setShowMobileModal(true);
              }
            }
          }
        })
        .catch(() => {});
    }
  }, [storeStatus, storeInfo]);

  const handleSaveMobile = (mobileNumber) => {
    localStorage.setItem("customerMobile", mobileNumber);
    sessionStorage.setItem("customerMobile", mobileNumber);
    localStorage.setItem("customerMobileTimestamp", String(Date.now()));
    sessionStorage.setItem("customerMobileTimestamp", String(Date.now()));
    setShowMobileModal(false);
  };

  return (
    <>
      {storeStatus === "NOT_FOUND" ? (
        <div className="inactive-container">
          <div className="inactive-card">
            <div className="inactive-emoji">🍽️</div>
            <h1>Restaurant Not Found</h1>
            <p>
              This restaurant does not exist or has been permanently removed. Please verify the URL or visit our homepage.
            </p>
            <a
              href="https://aduppankarai.com"
              className="btn btn-primary rounded-pill px-4 py-2 mt-3 fw-bold text-white shadow-sm"
              style={{ textDecoration: "none" }}
            >
              Go to Aduppankarai Home
            </a>
          </div>
        </div>
      ) : storeStatus === "INACTIVE" ? (
        <div className="inactive-container">
          <div className="inactive-card">
            <div className="inactive-emoji">🔒</div>
            <h1>We're Currently Closed</h1>
            <p>
              This restaurant is temporarily unavailable. Please check back later
              or contact the restaurant for more information.
            </p>
          </div>
        </div>
      ) : (
        <>
          <Navbar />
          <div className="landing-shop-page">
            {/* Floating Active Order Tracker Badge */}
            {hasActiveOrder && (
            <div
              className="position-fixed bottom-0 start-50 translate-middle-x mb-3 p-3 bg-dark text-white rounded-4 shadow-lg d-flex align-items-center justify-content-between gap-3"
              style={{
                zIndex: 9999,
                border: "2px solid var(--primary, #6b21a8)",
                maxWidth: "92vw",
                width: "420px",
                backdropFilter: "blur(8px)",
              }}
            >
              <div>
                <span className="badge bg-warning text-dark fw-bold d-block mb-1" style={{ fontSize: "11px" }}>
                  ORDER IN PROGRESS
                </span>
                <small className="fw-semibold">Token #{activeOrderToken}</small>
              </div>
              <button
                className="btn btn-sm btn-primary fw-bold text-white rounded-pill px-3 py-2 text-nowrap shadow-sm"
                onClick={() => navigate("/payment")}
              >
                View Order ➔
              </button>
            </div>
          )}

          {visibleSections.home !== false && banners?.length > 0 && (
            <Home banners={banners} />
          )}
          {visibleSections.menus !== false && (
            <Menus
              menuItems={menuItems}
              categories={menuCategories}
              hasBanner={visibleSections.home !== false && banners?.length > 0}
            />
          )}
          {visibleSections.contact !== false && <Footer />}

          {/* Back to Top Floating Button */}
          {showBackToTop && (
            <button
              type="button"
              className="shop-back-to-top-btn"
              onClick={scrollToTop}
              title="Back to Top"
              aria-label="Back to Top"
            >
              <span className="arrow-up">▲</span>
              <span className="back-top-text">Top</span>
            </button>
          )}

          {/* Mandatory Mobile Number Modal for ORDER FLOW plan */}
          <CustomerMobileModal
            isOpen={showMobileModal}
            onSubmit={handleSaveMobile}
            storeName={storeInfo?.name}
          />
        </div>
        </>
      )}
    </>
  );
};

export default Landing;
